import {
    computeMealEffectiveGrams,
    computeMealScaleFactor,
    createInitialMealPortionState,
    defaultMealSizeId,
    mealSizeById,
    MEAL_GRAMS_DEFAULT,
    roundKcalPer100g,
    type MealPortionMode,
    type MealProductSize,
} from './mealSizePortion';

export type MealPortionOption = {
    multiplier: number;
    label: string;
};

export const MEAL_PORTION_OPTIONS: MealPortionOption[] = [
    { multiplier: 1 / 4, label: '1/4' },
    { multiplier: 1 / 3, label: '1/3' },
    { multiplier: 1 / 2, label: '1/2' },
    { multiplier: 1, label: '×1' },
    { multiplier: 2, label: '×2' },
    { multiplier: 3, label: '×3' },
    { multiplier: 4, label: '×4' },
    { multiplier: 5, label: '×5' },
];

export function mealPortionIsOne(multiplier: number): boolean {
    return !Number.isFinite(multiplier) || Math.abs(multiplier - 1) < 1e-9;
}

export function matchMealPortionOption(multiplier: number): MealPortionOption | undefined {
    return MEAL_PORTION_OPTIONS.find(o => Math.abs(o.multiplier - multiplier) < 1e-9);
}

export function applyMealPortion(baseKcal: number, mealScale: number): number {
    const base = roundKcalPer100g(baseKcal);
    if (!Number.isFinite(base) || base <= 0) return 0;
    const scale = mealScale > 0 ? mealScale : 1;
    return Math.max(1, roundKcalPer100g(base * scale));
}

export function formatPortionChipLabel(multiplier: number): string {
    const match = matchMealPortionOption(multiplier);
    return match?.label ?? `×${multiplier}`;
}

export function formatPortionTitleSuffix(multiplier: number): string {
    if (mealPortionIsOne(multiplier)) return '';
    return formatPortionChipLabel(multiplier);
}

export function formatPortionFactorLabel(multiplier: number): string {
    if (mealPortionIsOne(multiplier)) return '1';
    const match = matchMealPortionOption(multiplier);
    if (match) return match.label.startsWith('×') ? match.label.slice(1) : match.label;
    return String(multiplier);
}

export function formatPortionLogLabel(multiplier: number): string {
    if (mealPortionIsOne(multiplier)) return 'full portion';
    return `${formatPortionChipLabel(multiplier)} portion`;
}

export function initialPortionFromStoredLabels(
    sizes: MealProductSize[],
    portionLabel?: string | null,
    sizeLabel?: string | null,
): {
    portionMode: MealPortionMode;
    selectedSizeId: string | null;
    chipMultiplier: number;
    customGrams: number;
} {
    const base = createInitialMealPortionState(sizes);
    const portion = portionLabel?.trim();
    const size = sizeLabel?.trim();
    if (!portion && !size) return base;

    if (size) {
        const matchedSize = sizes.find(entry => entry.label === size);
        const chipOption = portion ? MEAL_PORTION_OPTIONS.find(option => option.label === portion) : undefined;
        return {
            portionMode: 'size',
            selectedSizeId: matchedSize?.id ?? base.selectedSizeId,
            chipMultiplier: chipOption?.multiplier ?? 1,
            customGrams: matchedSize?.grams ?? base.customGrams,
        };
    }

    if (portion?.endsWith('g')) {
        const grams = Number.parseInt(portion, 10);
        if (Number.isFinite(grams) && grams > 0) {
            return {
                portionMode: 'grams',
                selectedSizeId: base.selectedSizeId,
                chipMultiplier: 1,
                customGrams: grams,
            };
        }
    }

    if (portion) {
        const chipOption = MEAL_PORTION_OPTIONS.find(option => option.label === portion);
        if (chipOption) {
            return {
                portionMode: 'size',
                selectedSizeId: base.selectedSizeId,
                chipMultiplier: chipOption.multiplier,
                customGrams: base.customGrams,
            };
        }
    }

    return base;
}

export function buildStoredPortionFields(opts: {
    mode: MealPortionMode;
    sizes: MealProductSize[];
    selectedSizeId: string | null;
    chipMultiplier: number;
    customGrams: number;
}): { portionLabel: string | null; sizeLabel: string | null; portionGrams: number | null } {
    const ref = MEAL_GRAMS_DEFAULT;
    const effectiveGrams = computeMealEffectiveGrams(opts.mode, opts.sizes, opts.selectedSizeId, opts.chipMultiplier, opts.customGrams);
    const scaleFactor = computeMealScaleFactor(ref, effectiveGrams);

    if (!opts.sizes.length && opts.mode === 'size') {
        if (mealPortionIsOne(opts.chipMultiplier)) return { portionLabel: null, sizeLabel: null, portionGrams: null };
        return { portionLabel: formatPortionChipLabel(opts.chipMultiplier), sizeLabel: null, portionGrams: null };
    }

    if (opts.mode === 'grams' || !opts.sizes.length) {
        if (mealPortionIsOne(scaleFactor)) return { portionLabel: null, sizeLabel: null, portionGrams: null };
        return { portionLabel: `${effectiveGrams}g`, sizeLabel: null, portionGrams: effectiveGrams };
    }

    const size = mealSizeById(opts.sizes, opts.selectedSizeId) ?? mealSizeById(opts.sizes, defaultMealSizeId(opts.sizes));
    if (!size) {
        if (mealPortionIsOne(scaleFactor)) return { portionLabel: null, sizeLabel: null, portionGrams: null };
        return { portionLabel: `${effectiveGrams}g`, sizeLabel: null, portionGrams: effectiveGrams };
    }

    const portionLabel = mealPortionIsOne(opts.chipMultiplier) ? null : formatPortionChipLabel(opts.chipMultiplier);
    return { portionLabel, sizeLabel: size.label, portionGrams: effectiveGrams };
}

function isGramsPortionLabel(portion: string): boolean {
    return /^\d+(?:\.\d+)?g$/i.test(portion);
}

function localizeGramsPortionLabel(portion: string, gramUnit: string): string {
    return portion.replace(/g$/i, gramUnit);
}

export function formatStoredPortionDisplay(
    portionLabel?: string | null,
    sizeLabel?: string | null,
    portionGrams?: number | null,
    gramUnit = 'g',
    formatChipPortion?: (chipLabel: string) => string,
): string | null {
    const portion = portionLabel?.trim();
    const size = sizeLabel?.trim();
    const portionIsGrams = Boolean(portion && isGramsPortionLabel(portion));
    const localizedPortion = portion && portionIsGrams ? localizeGramsPortionLabel(portion, gramUnit) : portion;
    const gramsPart = portionGrams != null && portionGrams > 0 && !portionIsGrams ? `${portionGrams}${gramUnit}` : null;
    const chipPortion = (chip: string) => (formatChipPortion ? formatChipPortion(chip) : `${chip} portion`);

    if (size && localizedPortion) return gramsPart ? `${localizedPortion} ${size} · ${gramsPart}` : `${localizedPortion} ${size}`;
    if (size) return gramsPart ? `${size} · ${gramsPart}` : size;
    if (localizedPortion) return portionIsGrams ? localizedPortion : chipPortion(localizedPortion);
    return gramsPart;
}

export function formatMealPortionLogDisplay(opts: {
    mealScale: number;
    referenceGrams?: number;
    sizes?: MealProductSize[];
}): string | null {
    const ref = opts.referenceGrams != null && opts.referenceGrams > 0 ? opts.referenceGrams : MEAL_GRAMS_DEFAULT;
    const mult = opts.mealScale > 0 ? opts.mealScale : 1;
    const effectiveGrams = Number.isFinite(mult * ref) && mult * ref > 0 ? mult * ref : MEAL_GRAMS_DEFAULT;
    const gramsPart = `${effectiveGrams}g`;
    const sizes = opts.sizes ?? [];

    if (!sizes.length) {
        if (mealPortionIsOne(mult)) return null;
        return formatPortionChipLabel(mult);
    }

    let best: { size: MealProductSize; ratio: number; score: number } | null = null;
    for (const size of sizes) {
        if (size.grams <= 0) continue;
        const ratio = effectiveGrams / size.grams;
        const matched = matchMealPortionOption(ratio);
        const isOne = mealPortionIsOne(ratio);
        if (!isOne && !matched) continue;
        const score = Math.abs(ratio - (matched?.multiplier ?? 1));
        if (!best || score < best.score) best = { size, ratio, score };
    }

    if (!best) return gramsPart;

    const { size, ratio } = best;
    if (mealPortionIsOne(ratio)) return `${size.label} · ${gramsPart}`;
    return `${formatPortionChipLabel(ratio)} ${size.label} · ${gramsPart}`;
}

export type MealPortionFields = {
    baseKcal: number;
    mealScale: number;
};

export type MealPortionRecord = {
    kcal: number;
    baseKcal?: number | null;
    portionLabel?: string | null;
};

function mealScaleFromPortionLabel(portionLabel?: string | null): number | null {
    const portion = portionLabel?.trim();
    if (!portion) return null;
    const chipOption = MEAL_PORTION_OPTIONS.find(option => option.label === portion);
    return chipOption?.multiplier ?? null;
}

export function resolveMealPortionFields(input: MealPortionRecord): MealPortionFields {
    const kcal = roundKcalPer100g(input.kcal);
    const labelScale = mealScaleFromPortionLabel(input.portionLabel);
    if (input.baseKcal != null && input.baseKcal > 0) {
        const baseKcal = roundKcalPer100g(input.baseKcal);
        const kcalScale = kcal > 0 ? kcal / baseKcal : 1;
        const mealScale = labelScale != null && !mealPortionIsOne(labelScale) ? labelScale : kcalScale > 0 ? kcalScale : 1;
        return { baseKcal, mealScale: mealScale > 0 ? mealScale : 1 };
    }
    if (labelScale != null && !mealPortionIsOne(labelScale) && kcal > 0) {
        return { baseKcal: roundKcalPer100g(kcal / labelScale), mealScale: labelScale };
    }
    return { baseKcal: kcal > 0 ? kcal : 0, mealScale: 1 };
}

export function resolveMealLabelFromLegacyKcalNote(label: string, kcalNote: string | null | undefined): string | null {
    if (!kcalNote?.trim()) return null;
    const parsed = parseTrackedMealKcalNote(kcalNote);
    if (!parsed) return null;
    const mealName = parsed.mealName?.trim();
    if (!mealName || mealName === label.trim()) return null;
    return mealName;
}

export function trackedMealNameKcalNote(mealName: string, fallbackLabel: string): string | undefined {
    const meal = mealName.trim();
    const label = fallbackLabel.trim();
    if (!meal || meal === label) return undefined;
    return meal;
}

export function extractPortionLabelFromKcalNote(
    kcalNote: string | null | undefined,
    kcal: number | null | undefined,
): {
    portionLabel?: string;
    baseKcal?: number;
} | null {
    if (!kcalNote?.trim()) return null;
    const trimmed = kcalNote.trim();
    if (!trimmed.startsWith('{')) return null;

    const parsed = parseTrackedMealKcalNote(trimmed);
    if (!parsed) return null;

    const portion = parsed.mealScale > 0 ? parsed.mealScale : 1;
    const hasPortion = !mealPortionIsOne(portion);
    const hasBase = parsed.baseKcal > 0;
    if (!hasPortion && !hasBase) return null;

    const totalKcal = kcal ?? 0;
    const baseKcal = hasBase ? parsed.baseKcal : totalKcal > 0 ? Math.max(1, Math.round(totalKcal / portion)) : undefined;

    return {
        ...(hasPortion ? { portionLabel: formatPortionChipLabel(portion) } : {}),
        baseKcal,
    };
}

export function extractPortionFromKcalNote(
    kcalNote: string | null | undefined,
    kcal: number | null | undefined,
): {
    baseKcal?: number;
    mealScale?: number;
} | null {
    const patch = extractPortionLabelFromKcalNote(kcalNote, kcal);
    if (!patch) return null;
    const parsed = parseTrackedMealKcalNote(kcalNote!.trim());
    if (!parsed) return null;
    const mealScale = parsed.mealScale > 0 ? parsed.mealScale : 1;
    return {
        baseKcal: patch.baseKcal,
        mealScale,
    };
}

export type TrackedMealKcalNoteMeta = {
    mealName?: string;
    baseKcal: number;
    mealScale: number;
};

export function encodeTrackedMealKcalNote(mealName: string, baseKcal: number, mealScale: number): string | undefined {
    const meal = mealName.trim();
    const base = roundKcalPer100g(baseKcal);
    const scale = mealScale > 0 ? mealScale : 1;
    if (!meal && mealPortionIsOne(scale)) return undefined;
    if (mealPortionIsOne(scale) && !meal) return undefined;
    if (!mealPortionIsOne(scale) && base <= 0) return undefined;
    return JSON.stringify({ m: meal || undefined, b: base, p: scale });
}

export function parseTrackedMealKcalNote(note: string): TrackedMealKcalNoteMeta | null {
    const trimmed = note.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('{')) {
        try {
            const raw = JSON.parse(trimmed) as { m?: string; b?: number; p?: number };
            if (typeof raw.b === 'number' && typeof raw.p === 'number' && raw.b > 0 && raw.p > 0) {
                const matched = matchMealPortionOption(raw.p);
                return {
                    mealName: typeof raw.m === 'string' ? raw.m.trim() : undefined,
                    baseKcal: Math.round(raw.b),
                    mealScale: matched?.multiplier ?? raw.p,
                };
            }
        } catch {
            return null;
        }
        return null;
    }
    return { mealName: trimmed, baseKcal: 0, mealScale: 1 };
}

export function resolveTrackedMealIntakeDisplay(entry: { kcal: number; cardLabel: string; baseKcal?: number | null }): {
    title: string;
    subline: string;
    totalKcal: number;
} {
    const totalKcal = entry.kcal;
    const portionFields = resolveMealPortionFields({
        kcal: totalKcal,
        baseKcal: entry.baseKcal,
    });
    if (mealPortionIsOne(portionFields.mealScale)) {
        const label = entry.cardLabel.trim();
        if (label) {
            return { title: label, subline: `${totalKcal} kcal`, totalKcal };
        }
        return { title: entry.cardLabel, subline: 'Calories logged', totalKcal };
    }
    const baseKcal = portionFields.baseKcal > 0 ? portionFields.baseKcal : Math.max(1, Math.round(totalKcal / portionFields.mealScale));
    const meal = entry.cardLabel.trim();
    const suffix = formatPortionTitleSuffix(portionFields.mealScale);
    const title = suffix ? `${meal} ${suffix}` : meal;
    const subline = `${baseKcal} kcal × ${formatPortionFactorLabel(portionFields.mealScale)}`;
    return { title, subline, totalKcal };
}
